
import { db } from '../../lib/firebase';
import { uploadDeliveryFile } from './files';
import { 
    NotificationSettings, FinancialSettings, InvoiceSettings, 
    BrandingSettings, MarketplaceSettings, IntegrationSecrets,
    NotificationTemplate, ModuleSettings, LegalPageSettings,
    TerminologySettings, PlatformSettings, DashboardSectionConfig
} from '../../types';

// Helper to resolve document ref (Global vs Tenant)
const getConfigRef = (collection: string, tenantId?: string) => {
    if (tenantId) {
        // Tenant Config is stored in a map inside the tenant document
        // This helper returns the doc ref, but updating a specific field requires dot notation
        return db.collection('tenants').doc(tenantId);
    }
    return db.collection('settings').doc(collection);
};

const DEFAULT_DASHBOARD_SECTIONS: DashboardSectionConfig[] = [
    { id: 'welcome_header', label: 'Welcome Header', enabled: true, order: 0, description: 'User greeting and date' },
    { id: 'thank_you', label: 'Thank You Banner', enabled: true, order: 1, description: 'Shows for Clients with completed orders' },
    { id: 'studio_links', label: 'Access Links', enabled: true, order: 2, description: 'Admin Only: Portal URLs' },
    { id: 'stats_cards', label: 'Statistics Grid', enabled: true, order: 3, description: 'Active, Pending, Revenue/Total cards' },
    { id: 'upsell_grid', label: 'Featured Services (Upsell)', enabled: true, order: 4, description: 'Random selection of services' },
    { id: 'quick_actions', label: 'Quick Actions', enabled: true, order: 5, description: 'Start Project, View Files, Support' }
];

export const getPlatformSettings = async (): Promise<PlatformSettings> => {
    try {
        const snap = await db.collection('settings').doc('platform').get();
        if (snap.exists) {
            const data = snap.data() as PlatformSettings;
            // Merge defaults for new feature
            if (!data.dashboardSections) {
                data.dashboardSections = DEFAULT_DASHBOARD_SECTIONS;
            }
            return data;
        }
    } catch(e) {}
    return { dashboardSections: DEFAULT_DASHBOARD_SECTIONS };
};

export const updatePlatformSettings = async (settings: PlatformSettings) => {
    await db.collection('settings').doc('platform').set(settings, { merge: true });
};

export const getNotificationSettings = async (tenantId?: string): Promise<NotificationSettings> => {
    try {
        if (tenantId) {
            const snap = await db.collection('tenants').doc(tenantId).get();
            return snap.data()?.config?.notifications || { enableEmails: true, ownerEmail: '', contactEmail: '' };
        }
        const snap = await db.collection('settings').doc('notifications').get();
        if (snap.exists) return snap.data() as NotificationSettings;
    } catch(e) {}
    return { enableEmails: true, ownerEmail: '', contactEmail: '' };
};

export const updateNotificationSettings = async (settings: NotificationSettings, tenantId?: string) => {
    if (tenantId) {
        // Use set merge to allow auto-creation of root_platform document
        await db.collection('tenants').doc(tenantId).set({ 
            config: { notifications: settings } 
        }, { merge: true });
    } else {
        await db.collection('settings').doc('notifications').set(settings);
    }
};

export const getFinancialSettings = async (tenantId?: string): Promise<FinancialSettings> => {
    const defaults = { defaultStripeProductId: '', stripeEnabled: true, szoinPriceId: '', allowTenantStripe: true };
    try {
        if (tenantId) {
            const snap = await db.collection('tenants').doc(tenantId).get();
            const data = snap.data()?.config?.financial || {};
            return { ...defaults, ...data };
        }
        const snap = await db.collection('settings').doc('financial').get();
        if (snap.exists) return { ...defaults, ...snap.data() };
    } catch(e) {}
    return defaults;
};

export const updateFinancialSettings = async (settings: FinancialSettings, tenantId?: string) => {
    if (tenantId) {
        await db.collection('tenants').doc(tenantId).set({ 
            config: { financial: settings } 
        }, { merge: true });
    } else {
        await db.collection('settings').doc('financial').set(settings);
    }
};

export const getInvoiceSettings = async (tenantId?: string): Promise<InvoiceSettings> => {
    const defaults = { businessName: '', address: '', email: '', legalRegion: 'PK' as const };
    try {
        if (tenantId) {
            const snap = await db.collection('tenants').doc(tenantId).get();
            const data = snap.data()?.config?.invoice || {};
            return { ...defaults, ...data };
        }
        const snap = await db.collection('settings').doc('invoice').get();
        if (snap.exists) return snap.data() as InvoiceSettings;
    } catch(e) {}
    return defaults;
};

export const updateInvoiceSettings = async (settings: InvoiceSettings, tenantId?: string) => {
    if (tenantId) {
        await db.collection('tenants').doc(tenantId).set({ 
            config: { invoice: settings } 
        }, { merge: true });
    } else {
        await db.collection('settings').doc('invoice').set(settings);
    }
};

export const uploadInvoiceLogo = async (file: File): Promise<string> => {
    return uploadDeliveryFile(file, `settings/invoice_logo_${Date.now()}`);
};

export const getBrandingSettings = async (tenantId?: string): Promise<BrandingSettings> => {
    const defaults: BrandingSettings = { 
        appTitle: 'My Studio',
        developerName: 'Platform Provider',
        developerUrl: ''
    };
    try {
        if (tenantId) {
            // Check Tenant Override
            const snap = await db.collection('tenants').doc(tenantId).get();
            const data = snap.data()?.config?.branding;
            if (data) return { ...defaults, ...data };
            
            // If no tenant branding, try Global fallback
            const globalSnap = await db.collection('settings').doc('branding').get();
            if (globalSnap.exists) return { ...defaults, ...globalSnap.data() };
            return defaults;
        }

        const snap = await db.collection('settings').doc('branding').get();
        if (snap.exists) {
            return { ...defaults, ...snap.data() } as BrandingSettings;
        }
    } catch(e) {
        console.warn("Offline or error loading branding", e);
    }
    return defaults;
};

export const updateBrandingSettings = async (settings: BrandingSettings, tenantId?: string) => {
    if (tenantId) {
        await db.collection('tenants').doc(tenantId).set({ 
            config: { branding: settings } 
        }, { merge: true });
    } else {
        await db.collection('settings').doc('branding').set(settings);
    }
};

export const uploadBrandingAsset = async (file: File): Promise<string> => {
    return uploadDeliveryFile(file, `settings/branding_${Date.now()}_${file.name}`);
};

export const getMarketplaceSettings = async (): Promise<MarketplaceSettings> => {
    try {
        const snap = await db.collection('settings').doc('marketplace').get();
        if (snap.exists) return snap.data() as MarketplaceSettings;
    } catch(e) {}
    // Default to DISABLED
    return { enabled: false, heroTitle: 'Creative Marketplace', heroSubtitle: 'Find professional studios and artists.' };
};

export const updateMarketplaceSettings = async (settings: MarketplaceSettings) => {
    await db.collection('settings').doc('marketplace').set(settings);
};

export const getIntegrationSecrets = async (tenantId?: string): Promise<IntegrationSecrets> => {
    // Secrets should NOT be stored in the public Tenant document.
    // Tenant secrets are stored in studios/{ownerId}/config/secrets or handled via env vars.
    // This function returns GLOBAL secrets.
    try {
        const snap = await db.collection('settings').doc('secrets').get();
        if (snap.exists) return snap.data() as IntegrationSecrets;
    } catch(e) {}
    return {};
};

export const updateIntegrationSecrets = async (secrets: IntegrationSecrets) => {
    await db.collection('settings').doc('secrets').set(secrets, { merge: true });
};

export const getModuleSettings = async (tenantId?: string): Promise<ModuleSettings> => {
    const defaults: ModuleSettings = { 
        enableBookings: true, 
        enableProjects: true, 
        enablePortfolio: true, 
        enableTeam: true, 
        enableFiles: true, 
        enableContact: true,
        enableWorkshops: true,
        enableMarketing: true,
        enableCRM: true,
        enableFinances: true,
        enableLegal: true,
        enableForms: true,
        // Enterprise Defaults
        enableAiAssistant: true,
        enableSms: true,
        enableAnalytics: true,
        enableClientPortal: true
    };

    try {
        if (tenantId) {
            const snap = await db.collection('tenants').doc(tenantId).get();
            const data = snap.data()?.config?.modules;
            if (data) return { ...defaults, ...data };
            // Fallback to global defaults if tenant hasn't configured modules yet
        }
        
        const snap = await db.collection('settings').doc('modules').get();
        if (snap.exists) {
            return { ...defaults, ...snap.data() } as ModuleSettings;
        }
    } catch(e) {}
    
    return defaults;
};

export const updateModuleSettings = async (settings: ModuleSettings, tenantId?: string) => {
    if (tenantId) {
        await db.collection('tenants').doc(tenantId).set({ 
            config: { modules: settings } 
        }, { merge: true });
    } else {
        await db.collection('settings').doc('modules').set(settings);
    }
};

export const getLegalPageSettings = async (tenantId?: string): Promise<LegalPageSettings> => {
    try {
        if (tenantId) {
            const snap = await db.collection('tenants').doc(tenantId).get();
            return snap.data()?.config?.legalPages || {};
        }
        const snap = await db.collection('settings').doc('legal_pages').get();
        if (snap.exists) return snap.data() as LegalPageSettings;
    } catch(e) {}
    return {};
};

export const updateLegalPageSettings = async (settings: LegalPageSettings, tenantId?: string) => {
    if (tenantId) {
        await db.collection('tenants').doc(tenantId).set({ 
            config: { legalPages: settings } 
        }, { merge: true });
    } else {
        await db.collection('settings').doc('legal_pages').set(settings);
    }
};

// --- TERMINOLOGY ---

export const getGlobalTerminology = async (): Promise<TerminologySettings | null> => {
    try {
        const snap = await db.collection('settings').doc('terminology').get();
        if (snap.exists) return snap.data() as TerminologySettings;
    } catch(e) {}
    return null;
};

export const updateGlobalTerminology = async (terms: TerminologySettings) => {
    await db.collection('settings').doc('terminology').set(terms);
};

export const updateTenantTerminology = async (tenantId: string, terms: TerminologySettings) => {
    // Updates the terminology object inside the 'config' map of the tenant document
    await db.collection('tenants').doc(tenantId).set({
        config: {
            terminology: terms
        }
    }, { merge: true });
};

// --- NOTIFICATION TEMPLATES (FULLY DYNAMIC) ---

const DEFAULT_TEMPLATES: NotificationTemplate[] = [
    {
        id: 'order_received',
        name: 'Order Received',
        message: "Hi {{client_name}}, your request for {{service_name}} has been received.\nStatus: Processing.\nWe’ll notify you once it’s reviewed.\n\n— {{studio_name}}",
        enabled: true,
        variables: ['client_name', 'service_name', 'studio_name'],
        triggerDescription: 'Triggers when a client submits a new booking request.'
    },
    {
        id: 'contract_uploaded',
        name: 'Contract Uploaded',
        message: "Hi {{client_name}}, a document requires your signature.\nPlease review and sign to proceed.\n\n— {{studio_name}}",
        enabled: true,
        variables: ['client_name', 'studio_name'],
        triggerDescription: 'Triggers when studio sends an agreement/NDA.'
    },
    {
        id: 'contract_accepted',
        name: 'Contract Accepted',
        message: "Agreement accepted for {{service_name}}.\nYour project is now moving to the next stage.\n\n— {{studio_name}}",
        enabled: true,
        variables: ['service_name', 'studio_name'],
        triggerDescription: 'Triggers when client signs the agreement.'
    },
    {
        id: 'order_approved',
        name: 'Order Approved',
        message: "Great news {{client_name}}!\nYour request has been approved.\nPlease complete payment to confirm your booking.\n{{payment_link}}\n\n— {{studio_name}}",
        enabled: true,
        variables: ['client_name', 'payment_link', 'studio_name'],
        triggerDescription: 'Triggers when studio approves a request.'
    },
    {
        id: 'payment_received',
        name: 'Payment Received',
        message: "Payment received.\nYour booking is confirmed for {{date}} at {time}.\n\n— {{studio_name}}",
        enabled: true,
        variables: ['date', 'time', 'studio_name'],
        triggerDescription: 'Triggers when payment is confirmed.'
    },
    {
        id: 'order_cancelled',
        name: 'Order Cancelled',
        message: "Your order has been cancelled.\nPlease contact us if you have questions.\n\n— {{studio_name}}",
        enabled: true,
        variables: ['studio_name'],
        triggerDescription: 'Triggers when an order is rejected or cancelled.'
    },
    {
        id: 'new_client_signup',
        name: 'New Client Alert (Staff)',
        message: "New Client Registration: {{client_name}} has joined your studio portal.\n\n— {{studio_name}} System",
        enabled: true,
        variables: ['client_name', 'studio_name'],
        triggerDescription: 'Triggers to OWNER when a new client registers.'
    }
];

export const getNotificationTemplates = async (): Promise<NotificationTemplate[]> => {
    try {
        const snap = await db.collection('notification_templates').get();
        if (snap.empty) {
            // Attempt seed, might fail if permissions denied (e.g. non-SuperAdmin in SaaS)
            try { await seedNotificationTemplates(); } catch (seedErr) {}
            return DEFAULT_TEMPLATES;
        }
        return snap.docs.map(d => ({ id: d.id, ...d.data() } as NotificationTemplate));
    } catch (e) {
        return DEFAULT_TEMPLATES; // Fallback to avoid UI crash
    }
};

export const updateNotificationTemplate = async (template: NotificationTemplate) => {
    await db.collection('notification_templates').doc(template.id).set(template, { merge: true });
};

export const seedNotificationTemplates = async () => {
    const batch = db.batch();
    for (const t of DEFAULT_TEMPLATES) {
        const ref = db.collection('notification_templates').doc(t.id);
        batch.set(ref, t);
    }
    await batch.commit();
};
